import { z } from 'zod';
/**
 * Convierte a numero un texto que puede venir en formato chileno.
 *
 * - `"9884,000"` -> `9884` (la coma es separador decimal)
 * - `"1.234,50"` -> `1234.5` (el punto separa miles, la coma decimales)
 * - `"18.0"`     -> `18` (solo punto: se interpreta como separador decimal,
 *   igual que un numero JSON)
 *
 * Devuelve `null` si el texto no es un numero reconocible o si es ambiguo
 * (varias comas, caracteres sobrantes). Ambiguo se rechaza: preferimos
 * cuarentena a inventar una cantidad.
 */
export function parseLooseDecimal(raw) {
    const text = raw.trim();
    if (text === '') {
        return null;
    }
    const hasComma = text.includes(',');
    const hasDot = text.includes('.');
    let normalized = text;
    if (hasComma && hasDot) {
        normalized = text.replace(/\./g, '').replace(',', '.');
    }
    else if (hasComma) {
        normalized = text.replace(',', '.');
    }
    if (!/^[+-]?\d+(\.\d+)?$/.test(normalized)) {
        return null;
    }
    const value = Number(normalized);
    return Number.isFinite(value) ? value : null;
}
/** Aplica min/max y anota el issue correspondiente. Devuelve `false` si fallo. */
function checkRange(value, options, ctx) {
    if (options.min !== undefined && value < options.min) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `El valor ${value} es menor que el minimo permitido (${options.min}).`,
        });
        return false;
    }
    if (options.max !== undefined && value > options.max) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `El valor ${value} supera el maximo permitido (${options.max}).`,
        });
        return false;
    }
    return true;
}
/**
 * Numero que puede llegar como `number` o como texto numerico (incluido el
 * formato con coma decimal). Salida siempre `number`.
 */
export function looseDecimal(options = {}) {
    return z
        .union([z.number(), z.string()], {
        errorMap: () => ({ message: 'Se esperaba un numero o un texto numerico.' }),
    })
        .transform((value, ctx) => {
        const parsed = typeof value === 'number' ? value : parseLooseDecimal(value);
        if (parsed === null || !Number.isFinite(parsed)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `No es un numero valido: ${JSON.stringify(value)}.`,
            });
            return z.NEVER;
        }
        if (!checkRange(parsed, options, ctx)) {
            return z.NEVER;
        }
        return parsed;
    });
}
/** Entero que puede llegar como `number` o como texto. Salida siempre `number`. */
export function looseInteger(options = {}) {
    return z
        .union([z.number(), z.string()], {
        errorMap: () => ({ message: 'Se esperaba un entero o un texto numerico.' }),
    })
        .transform((value, ctx) => {
        const parsed = typeof value === 'number' ? value : parseLooseDecimal(value);
        if (parsed === null || !Number.isFinite(parsed)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `No es un entero valido: ${JSON.stringify(value)}.`,
            });
            return z.NEVER;
        }
        if (!Number.isInteger(parsed)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Se esperaba un entero y llego ${parsed}.`,
            });
            return z.NEVER;
        }
        if (!checkRange(parsed, options, ctx)) {
            return z.NEVER;
        }
        return parsed;
    });
}
/** Codigo corto de un sistema externo: bodega, empresa, tipo de documento. */
export function codeSchema(maxLength = 32) {
    return z
        .string()
        .trim()
        .min(1, 'El codigo no puede venir vacio.')
        .max(maxLength, `El codigo no puede superar ${maxLength} caracteres.`);
}
/** Texto obligatorio y no vacio. */
export function requiredText(maxLength = 512) {
    return z
        .string()
        .trim()
        .min(1, 'El texto no puede venir vacio.')
        .max(maxLength, `El texto no puede superar ${maxLength} caracteres.`);
}
/**
 * Texto opcional. Muchos sistemas externos mandan `""` en vez de omitir el
 * campo (Altanet lo hace con `Lote`, `Notas`, las fechas de lote): la cadena
 * vacia se normaliza a `undefined` para que el consumidor no tenga que
 * distinguir entre "vacio" y "ausente".
 */
export function optionalText(maxLength = 512) {
    return z
        .string()
        .max(maxLength, `El texto no puede superar ${maxLength} caracteres.`)
        .transform((value) => {
        const trimmed = value.trim();
        return trimmed === '' ? undefined : trimmed;
    })
        .optional();
}
/**
 * Texto opcional que ademas acepta `null`, normalizandolo a `undefined`.
 *
 * ## Por que hace falta un helper aparte
 *
 * `optionalText` distingue "ausente" de "vacio" pero **rechaza `null`**, y hay
 * proveedores que mandan `null` en vez de omitir el campo. Janis es el caso
 * extremo: en el spec del OMS (1.386.0) casi todas las propiedades del cliente y
 * de la direccion del pedido estan declaradas `nullable: true`, asi que un
 * pedido real perfectamente valido llega con `"lastName": null`.
 *
 * Con `optionalText` eso es un `invalid_type` y el pedido se va a cuarentena por
 * un campo que el flujo ni siquiera necesita. Que es justo el modo de fallo que
 * el paquete declara querer evitar en lo que RECIBIMOS (regla 3).
 *
 * Usalo **solo en lo que recibimos**. En lo que emitimos, un `null` nuestro es
 * un error nuestro y tiene que verse.
 */
export function nullableText(maxLength = 512) {
    return optionalText(maxLength)
        .nullable()
        .transform((value) => value ?? undefined);
}
/** Fecha-hora ISO 8601 con zona (`2026-08-26T13:52:04.117Z` o con offset). */
export const isoDateTimeSchema = z.string().trim().datetime({
    offset: true,
    message: 'Se esperaba una fecha-hora ISO 8601 con zona (p. ej. 2026-08-26T13:52:04.117Z).',
});
/** `true` si (ano, mes, dia) existe en el calendario. */
export function isRealCalendarDate(year, month, day) {
    if (month < 1 || month > 12 || day < 1 || day > 31) {
        return false;
    }
    const date = new Date(Date.UTC(year, month - 1, day));
    return (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day);
}
/** Fecha ISO sin hora (`2026-08-26`). Es el formato de `DocDate` en SAP. */
export const isoDateSchema = z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Se esperaba una fecha yyyy-mm-dd.')
    .refine((value) => {
    const [year, month, day] = value.split('-').map(Number);
    return isRealCalendarDate(year ?? 0, month ?? 0, day ?? 0);
}, 'La fecha no existe en el calendario.');
/** URL http/https. Se usa para la URL del DTE y para los webhooks salientes. */
export const httpUrlSchema = z
    .string()
    .trim()
    .url('Se esperaba una URL valida.')
    .refine((value) => /^https?:\/\//i.test(value), 'La URL debe ser http o https.');
/**
 * Monto en pesos chilenos. Entero: el CLP no tiene decimales y aceptar
 * `399990.5` solo produciria descuadres contra SAP.
 */
export const clpAmountSchema = z
    .number({ invalid_type_error: 'El monto debe ser un numero en pesos, sin comillas.' })
    .int('Los montos en CLP se expresan en pesos enteros, sin decimales.')
    .nonnegative('El monto no puede ser negativo.');
/** Monto en CLP que admite signo (notas de credito, ajustes). */
export const clpSignedAmountSchema = z
    .number({ invalid_type_error: 'El monto debe ser un numero en pesos, sin comillas.' })
    .int('Los montos en CLP se expresan en pesos enteros, sin decimales.');
/**
 * Escala decimal maxima de cualquier cantidad de la plataforma.
 *
 * Es la de `numeric(18,4)`, que es como se guardan las cantidades en
 * `stock_state`. No es un numero arbitrario: un quinto decimal lo redondea
 * PostgreSQL **en silencio** al guardarlo, y despues aparece como una diferencia
 * de stock que nadie sabe explicar. Preferimos rechazarlo en el borde.
 */
export const QUANTITY_DECIMAL_SCALE = 4;
/** `true` si el numero cabe en `numeric(x,4)` sin que la base lo redondee. */
function fitsQuantityScale(value) {
    const escalado = value * 10 ** QUANTITY_DECIMAL_SCALE;
    // La tolerancia absorbe el error de coma flotante de multiplicar por 10.000
    // (2.5 * 10000 puede dar 24999.999999999996), sin dejar pasar un decimal real.
    return Math.abs(escalado - Math.round(escalado)) < 1e-6;
}
/** Base comun de las cantidades: numero finito y con escala de 4 decimales. */
function quantityBase() {
    return z
        .number({ invalid_type_error: 'La cantidad debe ser un numero.' })
        .finite('La cantidad debe ser un numero finito.')
        .refine(fitsQuantityScale, `La cantidad no puede tener mas de ${String(QUANTITY_DECIMAL_SCALE)} decimales.`);
}
/**
 * Cantidad de una LINEA de pedido, factura o devolucion. Mayor que cero.
 *
 * **Admite decimales**, y eso es el arreglo de una limitacion real: era
 * `.int()`, asi que no se podia vender 2,5 m de tela. Los SKU por metro (telas,
 * cortes) son venta normal de tienda y quedaban fuera del contrato.
 *
 * El cero sigue prohibido: una linea sin cantidad no es una linea. Para las
 * cantidades que SI pueden ser cero —el stock— esta `stockQuantitySchema`.
 */
export const quantitySchema = quantityBase().refine((value) => value > 0, 'La cantidad debe ser mayor que cero.');
/**
 * Cantidad de STOCK. Cero incluido.
 *
 * Existe aparte de `quantitySchema` por un fallo concreto: el `stock` del
 * `stock-batch` de Janis y las cantidades del snapshot de SAP usaban la regla de
 * linea (`> 0` y entera), asi que **publicar un cero reventaba en la validacion
 * del lote**, antes de salir. Y publicar cero es justo como se apaga la venta de
 * un SKU agotado, que es medio F3.
 *
 * Que Janis acepta el cero esta VERIFICADO: su propio mensaje de error nombra el
 * tipo esperado, `number & positiveOrZero`, y el fixture real
 * `wms-stock-batch-aceptado.json` es una posicion con `stock: 0` que acepto.
 *
 * Negativo sigue prohibido: `computePublishedStock` y `computeSapUncommittedStock`
 * ponen piso en cero, y publicar un negativo a un marketplace es un error del
 * canal, no un descuento.
 *
 * ✅ **La divergencia con el borde de Janis quedo resuelta el 2026-09-07**, y
 * conviene entender que NO se resolvio igualando los dos schemas.
 *
 * Este schema sigue admitiendo decimales, porque describe el modelo INTERNO y
 * `stock_state` es `numeric(18,4)`: lo que SAP devuelve se guarda tal cual, sin
 * redondear, y eso es deliberado —el dato crudo es la evidencia—. El borde de
 * Janis sigue exigiendo entero (`stockAmountSchema` en `../janis/stock.js`)
 * porque el negocio confirmo que **no vendemos SKU por fraccion**: no hay
 * huinchas por centimetro ni telas por metro en el catalogo, asi que publicar
 * entero no pierde nada.
 *
 * Lo que faltaba no era alinear los schemas, era el PUENTE entre ellos, y su
 * ausencia era un bug vivo: `janisStockBatchRequestSchema.parse()` valida el
 * arreglo todo o nada, asi que un solo 9,8 —que SAP puede producir con una
 * conversion de unidad o un ajuste de inventario, aunque el SKU se venda por
 * unidad— lanzaba un ZodError y se llevaba la corrida completa. El puente es
 * `floorToPublishableStock`, y trunca hacia abajo por la misma razon por la que
 * `computePublishedStock` pone piso en cero: publicar de mas es una sobreventa.
 */
export const stockQuantitySchema = quantityBase().refine((value) => value >= 0, 'La cantidad de stock no puede ser negativa.');
/** SKU / codigo de producto. Mismo valor en Janis, SAP (`ItemCode`) y el WMS. */
export const skuSchema = z
    .string()
    .trim()
    .min(1, 'El SKU no puede venir vacio.')
    .max(64, 'El SKU no puede superar 64 caracteres.');
/** Codigo de bodega. Define si la linea la despacha el WMS o se entrega en tienda. */
export const warehouseCodeSchema = codeSchema(16);
//# sourceMappingURL=coercion.js.map